import { useState, useEffect } from 'react';
import { ParkingArea } from '../../types/parking';
import { StorageUnit } from '../../types/storageUnits';
import { Unit } from '../../types/units';
import { LevelModel } from '../../types/protocols';
import { parkingApi } from '../../api/parkingApi';
import { storageUnitsApi } from '../../api/storageUnitsApi';
import { unitsApi } from '../../api/unitsApi';
import { levelsApi } from '../../api/levelsApi';
import { naturalCompare } from '../../Helpers/naturalSort';
import { sortedStorageUnits } from '../../Helpers/storageUnitUtils';
import { sortedUnits } from '../../Helpers/unitUtils';

export interface LeaseBuildingObjects {
    areas: ParkingArea[];
    storageUnits: StorageUnit[];
    levels: { [levelKey: string]: LevelModel };
    rentalUnits: Unit[];
}

const EMPTY: LeaseBuildingObjects = { areas: [], storageUnits: [], levels: {}, rentalUnits: [] };

interface UseLeaseBuildingObjectsReturn {
    objects: LeaseBuildingObjects;
    loading: boolean;
    error: string | null;
}

/**
 * What LeaseModal's object picker needs for one building, read from the
 * building's subpaths. Empty while no building is chosen; reloads when the
 * building or `version` changes (bump it after a lease mutation).
 */
export const useLeaseBuildingObjects = (buildingKey: string | undefined, version = 0): UseLeaseBuildingObjectsReturn => {
    const [objects, setObjects] = useState<LeaseBuildingObjects>(EMPTY);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!buildingKey) {
            setObjects(EMPTY);
            setError(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        Promise.all([
            parkingApi.getAreas(buildingKey),
            storageUnitsApi.getUnits(buildingKey),
            levelsApi.getLevels(buildingKey),
            unitsApi.getUnits(buildingKey),
        ])
            .then(([areas, storageUnits, levels, units]) => {
                if (cancelled) return;
                setObjects({
                    areas: Object.values(areas).sort((a, b) => naturalCompare(a.name || '', b.name || '')),
                    storageUnits: sortedStorageUnits(storageUnits),
                    levels,
                    rentalUnits: sortedUnits(units),
                });
            })
            .catch(err => {
                if (cancelled) return;
                console.error('Error loading building objects:', err);
                setObjects(EMPTY);
                setError('Kunde inte ladda byggnadens hyresobjekt');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [buildingKey, version]);

    return { objects, loading, error };
};
